import { Theme, css, useTheme } from '@emotion/react';

import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';

import { SyntheticEvent } from 'react';

import { useUserQuery } from '@hooks/query/useUserQuery';
import { Radius, Sizes, Spacing, Typography } from 'chatie-design-system';
import {
  ArrowRight,
  Comment,
  DoubleBalloon,
  Icon,
  Tap,
} from 'chatie-design-system/icon';

import EFinderImage, {
  EFinderImageSizes,
} from '@components/common/EFinderImage';
import RoundAvatar from '@components/common/RoundAvatar';
import SvgIcon from '@components/common/SvgIcon';

import ProfileImg from '@public/icons/profile_32.svg';

import { fetchCharacters } from '@api/chatbot';
import { IProfileByInfoSectionFromList } from '@base/outlookModel';
import { Platform, QUERY_KEYS } from '@constants';
import { useGlobalContext } from '@contexts/global';
import { formatNumberMillion } from '@utils/chat';
import { toQueryString } from '@utils/misc';
import { useChatPaginationQuery } from '@utils/query';
import { useWebView } from '@utils/webview';

import Divider from './Divider';

const peekListTitle = (theme: Theme) => css`
  display: flex;
  justify-content: space-between;
  padding: 10px 16px;
  background-color: ${theme.colors.main10};
  margin-bottom: 4px;

  .arrow-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    top: 50%;
    gap: 2px;
    color: ${theme.var.gray500};

    p {
      ${Typography.body12};
      display: flex;
      align-items: center;
      justify-content: center;
      margin-top: 0px;
    }
    i {
      margin-bottom: 0;
    }
  }
`;
const cardContainerCSS = (theme: Theme) => css`
  display: flex;
  padding: ${Spacing.small}px;
  border-radius: ${Radius.small}px;

  .imageContainer {
    width: 50px;
    height: 50px;
    border-radius: ${Radius.small}px;
    margin-right: ${Spacing.small}px;
    position: relative;
    overflow: hidden;
    align-self: center;
  }
  .image {
    width: 50px;
    height: 50px;
    background-color: ${theme.var.gray200};
    border-radius: ${Radius.small}px;
    margin-right: ${Spacing.small}px;
  }
  .infoContainer {
    display: flex;
    flex-direction: column;
    flex: 1;
    width: 100%;
    overflow: hidden;
  }
  .name {
    ${Typography.h6};
  }
  .peek-ment {
    margin: ${Spacing.xxsmall}px 0px;
    ${Typography.body12};
    line-height: 145%;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }
  .tags-container {
    display: flex;
    flex-direction: row;
    gap: 4px;
    flex-wrap: wrap;
    max-height: 26px;
    white-space: nowrap;
    overflow: hidden;

    & > p {
      color: ${theme.var.gray400};
      ${Typography.body11};
    }
  }
  .stat-container {
    display: flex;
    margin-top: 10px;
    gap: 10px;
  }
  .tab-container,
  .balloon-container {
    display: flex;
    flex-direction: row;
    gap: 2px;

    & p {
      ${Typography.body12};
    }

    .tab {
      color: ${theme.colors.main500};
    }
    .balloon {
      color: ${theme.var.gray500};
    }
  }
`;

const profileCSS = css`
  display: flex;
  justify-content: flex-start;
  height: 12px;
  margin-top: ${Spacing.small}px;

  p {
    ${Typography.body12};
    margin-left: ${Spacing.xxhsmall}px;
  }
`;

interface ProfileByInfoSectionFromList {
  data: IProfileByInfoSectionFromList;
  componentKey: string;
}

const ProfileByInfoSectionFromList = ({
  data,
  componentKey,
}: ProfileByInfoSectionFromList) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { web } = useWebView();
  const { platform, goodsName, isCheming } = useGlobalContext();
  const router = useRouter();

  const userIdList = [
    ...new Set(
      data.profiles.map((item) => item.payload.dynamic_object.chat_room.user_id)
    ),
  ];
  const characterIdList = [
    ...new Set(data.profiles.map((item) => String(item.id))),
  ];

  const { useFetchUserList } = useUserQuery();
  const { data: userList } = useFetchUserList(userIdList);

  const characterQuery = useChatPaginationQuery(
    QUERY_KEYS.CHARACTER.LIST(characterIdList),
    async ({ pageParam }) =>
      await fetchCharacters({ ids: characterIdList }, pageParam)
  );
  const characterList = characterQuery.data?.pages[0].items;

  const handleClickCard = (id: number) => {
    if (platform === Platform.web) {
      router.push(`/play/chat-rooms/${id}/peek`);
    } else {
      web.openPage(`/play/chat-rooms/${id}/peek`);
    }
  };

  const handleClickCharacterProfile = (e: SyntheticEvent, id: number) => {
    e.stopPropagation();
    if (platform === Platform.web) {
      router.push(`/play/characters/${id}`);
    } else {
      web.openPage(`/play/characters/${id}`);
    }
  };

  const handleClickUserProfile = (e: SyntheticEvent, userId: number) => {
    e.stopPropagation();

    const url = `/users/${userId}`;
    if (platform === Platform.web) {
      return router.push(url);
    } else {
      web.openPage(url);
    }
  };

  const handleClickMore = () => {
    const url = `/play/peeks/${toQueryString({ componentKey })}`;
    if (platform === Platform.web) {
      router.push(url);
    }
    web.openPage(url);
  };

  return (
    <div>
      <div css={peekListTitle}>
        <h5>{data.title}</h5>
        <div className={'arrow-btn'} onClick={handleClickMore}>
          <p>{t('더보기')}</p>
          <Icon
            icon={ArrowRight}
            color={theme.var.gray500}
            size={Sizes.xsmall}
          />
        </div>
      </div>
      {data.profiles.map((profile, idx) => {
        const item = profile.payload.dynamic_object;
        const isAnonimous = !!item.chat_room.peek.is_anonymous;

        const user =
          userList &&
          !isAnonimous &&
          userList?.filter((user) => user.id === item.chat_room.user_id)[0];

        const tagList =
          characterList &&
          characterList?.filter((character) => character.id === profile.id)[0]
            ?.tags;

        return (
          <>
            <div
              css={cardContainerCSS}
              onClick={(e) => {
                if (isCheming) {
                  return handleClickCharacterProfile(e, profile.id);
                }
                handleClickCard(profile.payload.dynamic_object.chat_room.id);
              }}
              key={profile.id}
            >
              <div
                className="imageContainer"
                onClick={(e) => handleClickCharacterProfile(e, profile.id)}
              >
                {profile?.image && profile.image?.key ? (
                  <EFinderImage
                    image={profile.image}
                    h={EFinderImageSizes.avatar}
                    alt={'profile_img'}
                  />
                ) : (
                  <div className="image">
                    <SvgIcon icon={ProfileImg} d={50} />
                  </div>
                )}
              </div>
              <div className="infoContainer">
                <h6 className="name">{profile.title}</h6>
                <p className="peek-ment">{item.chat_room.peek.hooking_ment}</p>
                {tagList && tagList.length && (
                  <div className="tags-container">
                    {tagList.map((tag) => (
                      <p key={tag.id}>{t('#' + tag.name)}</p>
                    ))}
                  </div>
                )}
                <div className="stat-container">
                  <div className="tab-container">
                    <Icon
                      icon={Tap}
                      size={Sizes.xsmall}
                      color={theme.colors.main500}
                    />
                    <p className="tab">
                      {t(
                        formatNumberMillion(item.chat_room.peek.tap_count ?? 0)
                      )}
                    </p>
                  </div>

                  <div className="balloon-container">
                    <Icon
                      icon={DoubleBalloon}
                      size={Sizes.xsmall}
                      color={theme.var.gray500}
                    />
                    <p className="balloon">
                      {t(
                        formatNumberMillion(
                          item.chat_room.message_consumed_balloon ?? 0
                        ) + ` ${goodsName}`
                      )}
                    </p>
                  </div>

                  <div className="balloon-container">
                    <Icon
                      icon={Comment}
                      size={Sizes.xsmall}
                      color={theme.var.gray500}
                    />

                    <p className="balloon">
                      {t(
                        formatNumberMillion(
                          item?.chat_room.peek.comment_count ?? 0
                        )
                      )}
                    </p>
                  </div>
                </div>
                {!isAnonimous && user && (
                  <div
                    css={profileCSS}
                    onClick={(e) => handleClickUserProfile(e, user?.id)}
                  >
                    <RoundAvatar size={12} picture={user?.picture} />
                    <p>{t(user?.nickname)}</p>
                  </div>
                )}
              </div>
            </div>
            {data.profiles?.length - 1 !== idx && (
              <Divider data={{ height: 1 }} />
            )}
          </>
        );
      })}
    </div>
  );
};

export default ProfileByInfoSectionFromList;
